import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useSkillCollectionEditorSync } from "../hooks/useSkillCollectionEditorSync";
import type { PresetFile } from "../skill-presets";
import { SkillCollectionWorkspaceSkeleton } from "../SkillCollectionWorkspaceSkeleton";
import { SkillCollectionEditorProvider, useSkillCollectionEditorStore } from "../store";
import { buildServerTreeFromVersionSnapshot } from "../utils/tree-utils";

import { EditorLayout } from "./EditorLayout";
import { useEditorSaveReview } from "./hooks/useEditorSaveReview";
import { VersionReviewDialog } from "./VersionReviewDialog";

import {
  useGetSkillCollectionVersion,
  useListSkillCollectionFiles,
  useSaveSkillCollectionVersion,
} from "@/queries/skill-collections";
import type { SkillCollectionTreeNode } from "@/queries/skill-collections";

interface EditorTabContentProps {
  workspaceId: number;
  collectionId: string;
  rootPath?: string | null;
  provider?: string | null;
  versionNumber: number | null;
  initialSelectedPath?: string | null;
  onSelectedPathChange?: (path: string | null) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onSaveActionChange?: (saveAction: SkillCollectionSaveAction | null) => void;
  onSaveSuccess?: () => void;
}

export interface SkillCollectionSaveAction {
  isDirty: boolean;
  isSaving: boolean;
  onSave: () => void;
}

const EMPTY_SERVER_TREE: SkillCollectionTreeNode[] = [];

const EditorTabContentInner = observer(
  ({
    workspaceId,
    collectionId,
    rootPath,
    provider,
    versionNumber,
    initialSelectedPath,
    onSelectedPathChange,
    onDirtyChange,
    onSaveActionChange,
    onSaveSuccess,
  }: EditorTabContentProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const store = useSkillCollectionEditorStore();

    const { data: filesData, isLoading: isLoadingFiles } = useListSkillCollectionFiles(
      versionNumber ? null : workspaceId,
      versionNumber ? null : collectionId,
    );
    const { data: versionData, isLoading: isLoadingVersion } = useGetSkillCollectionVersion(
      workspaceId,
      collectionId,
      versionNumber,
    );
    const isLoadingVersionFiles = !!versionNumber && isLoadingVersion;
    const isLoadingLiveFiles = !versionNumber && isLoadingFiles;
    const saveMutation = useSaveSkillCollectionVersion({
      onSuccess: () => {
        onSaveSuccess?.();
      },
    });
    const presetFiles = (location.state as { presetFiles?: PresetFile[] } | null)?.presetFiles;

    const versionTree: SkillCollectionTreeNode[] | null = useMemo(() => {
      if (!versionData?.files) {
        return null;
      }

      return buildServerTreeFromVersionSnapshot(
        versionData.files,
        versionData.version.file_paths ?? undefined,
      );
    }, [versionData?.files, versionData?.version.file_paths]);

    const serverTree = versionTree ?? filesData?.files ?? EMPTY_SERVER_TREE;

    useSkillCollectionEditorSync({
      serverTree,
      rootPath,
      fileContents: versionData?.files ?? null,
      isSaving: saveMutation.isPending,
      presetFiles,
      pathname: location.pathname,
      navigate,
    });

    const {
      isReviewDialogOpen,
      savePhase,
      reviewSnapshot,
      effectiveProvider,
      hasProviderChange,
      handleProviderChange,
      handleOpenSaveReview,
      handleCloseReviewDialog,
      handleConfirmSave,
    } = useEditorSaveReview({
      store,
      provider,
      rootPath,
      saveMutation,
      workspaceId,
      collectionId,
      navigate,
    });
    const hasUnsavedChanges = store.isDirty || hasProviderChange;
    const hasCurrentSelectedPath = store.selectedPath
      ? store.filePaths.has(store.selectedPath)
      : false;
    const hasInitialSelectedPath = initialSelectedPath
      ? store.filePaths.has(initialSelectedPath)
      : false;

    useEffect(() => {
      if (store.selectedPath && hasCurrentSelectedPath) {
        return;
      }

      if (initialSelectedPath && hasInitialSelectedPath) {
        store.setSelectedPath(initialSelectedPath);
        return;
      }

      if (store.selectedPath && !hasCurrentSelectedPath) {
        store.setSelectedPath(null);
      }
    }, [
      hasCurrentSelectedPath,
      hasInitialSelectedPath,
      initialSelectedPath,
      store,
      store.selectedPath,
    ]);

    useEffect(() => {
      onSaveActionChange?.({
        isDirty: hasUnsavedChanges,
        isSaving: saveMutation.isPending,
        onSave: handleOpenSaveReview,
      });
    }, [handleOpenSaveReview, hasUnsavedChanges, onSaveActionChange, saveMutation.isPending]);

    useEffect(() => {
      onDirtyChange?.(hasUnsavedChanges);
    }, [hasUnsavedChanges, onDirtyChange]);

    useEffect(() => {
      onSelectedPathChange?.(store.selectedPath);
    }, [onSelectedPathChange, store.selectedPath]);

    useEffect(() => () => onSaveActionChange?.(null), [onSaveActionChange]);

    if (isLoadingVersionFiles || isLoadingLiveFiles) {
      return <SkillCollectionWorkspaceSkeleton />;
    }

    return (
      <>
        <EditorLayout
          versionFiles={versionData?.files ?? null}
          isLoadingVersionFiles={isLoadingVersionFiles}
          onProviderChange={handleProviderChange}
          provider={effectiveProvider}
        />
        <VersionReviewDialog
          isOpen={isReviewDialogOpen}
          reviewItems={reviewSnapshot}
          versionFiles={versionData?.files ?? null}
          isLoadingVersionSnapshot={isLoadingVersionFiles}
          isSaving={saveMutation.isPending}
          savePhase={savePhase}
          onClose={handleCloseReviewDialog}
          onConfirm={handleConfirmSave}
        />
      </>
    );
  },
);

const EditorTabContent = (props: EditorTabContentProps) => (
  <SkillCollectionEditorProvider>
    <EditorTabContentInner {...props} />
  </SkillCollectionEditorProvider>
);

export default EditorTabContent;
